import { useEffect, useRef } from 'react';

export default function Hero() {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(24px)';
    const t = setTimeout(() => {
      el.style.transition = 'opacity 1s ease, transform 1s ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    }, 120);
    return () => clearTimeout(t);
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Premium cinematic background — luxury real estate / inspection */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/7b506e41e72159fef92322c049c9f118.jpg"
          alt="Premium home inspection services nationwide"
          className="w-full h-full object-cover object-center"
        />
        {/* Heavy layered overlays — text must be crystal clear */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/97 via-[#050505]/82 to-[#050505]/45"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/75 via-transparent to-[#050505]/40"></div>
        {/* Extra depth layer on left where text lives */}
        <div className="absolute inset-y-0 left-0 w-3/4 bg-gradient-to-r from-[#050505]/50 to-transparent"></div>
      </div>

      {/* Content */}
      <div ref={contentRef} className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-14 pt-28 pb-20">
        <div className="max-w-3xl">

          {/* Eyebrow */}
          <div className="inline-flex items-center gap-3 mb-8">
            <div className="w-10 h-px bg-[#C9A961]"></div>
            <span className="text-[#C9A961] text-xs font-semibold uppercase tracking-[0.22em]">
              Serving the Greater Houston Area
            </span>
          </div>

          {/* H1 */}
          <h1
            className="text-5xl md:text-6xl lg:text-[4.5rem] font-bold text-white leading-[1.06] mb-7"
            style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
          >
            Texas Home
            <br />
            Inspections —{' '}
            <span className="text-[#C9A961]">Fast,</span>
            <br />
            <span className="text-[#C9A961]">Certified, Reliable.</span>
          </h1>

          {/* Sub */}
          <p
            className="text-lg md:text-xl text-white/90 leading-relaxed mb-10 max-w-2xl font-medium"
            style={{ textShadow: '0 1px 12px rgba(0,0,0,0.6)' }}
          >
            Serving Baytown, East Harris, Liberty, and Chambers Counties. Licensed inspectors, fast turnaround, clear reports.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={scrollToContact}
              className="bg-[#C9A961] text-white font-semibold text-base px-9 py-4 rounded-md hover:bg-[#b8944f] transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
            >
              Schedule Your Inspection
            </button>
            <a
              href="tel:+12814217355"
              className="flex items-center justify-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/50 text-white font-semibold text-base px-9 py-4 rounded-md hover:bg-white/18 hover:border-white/80 transition-all duration-200 whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-[#C9A961]"></i>
              </div>
              Call (281) 421-7355
            </a>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-8 mt-14 pt-10 border-t border-white/15">
            {[
              { value: '4', label: 'Counties Served' },
              { value: '15K+', label: 'Inspections Done' },
              { value: '30+', label: 'Years Experience' },
              { value: '5★', label: 'Rated Service' },
            ].map((s) => (
              <div key={s.label}>
                <div
                  className="text-3xl font-bold text-[#C9A961]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {s.value}
                </div>
                <div className="text-white/60 text-xs mt-0.5 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 opacity-60">
        <div className="w-px h-8 bg-gradient-to-b from-transparent to-white"></div>
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white text-sm"></i>
        </div>
      </div>
    </section>
  );
}
